import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Star, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Success() {
  const navigate = useNavigate();
  const location = useLocation();
  const submissionId = location.state?.submissionId;
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    const storedBranding = localStorage.getItem('company_branding');
    if (storedBranding) {
      setBranding(JSON.parse(storedBranding));
    }
  }, []);

  const handleReviewRedirect = () => {
    // In production, redirect to actual review platform
    window.open('https://www.avis-verifies.com', '_blank');
  };

  // Get branding colors with defaults
  const b = branding?.branding || {};
  const primaryColor = b.primary_color || '#f97316';
  const headerColor = b.header_color || '#ffffff';
  const bgColor = b.background_color || '#f8fafc';
  const logoUrl = b.logo_url ? (b.logo_url.startsWith('/') ? `${API_URL}${b.logo_url}` : b.logo_url) : null;
  const companyTitle = b.title || branding?.company_name || 'Litige-Protect';

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: bgColor }}>
      {/* Header */}
      <header 
        className="h-16 border-b backdrop-blur flex items-center justify-center px-6 gap-3"
        style={{ backgroundColor: headerColor + 'f5' }}
      >
        {logoUrl && (
          <img src={logoUrl} alt="Logo" className="h-8 w-8 object-contain rounded" />
        )}
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: primaryColor }}>
          {companyTitle}
        </h1>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md w-full space-y-8 text-center">
          {/* Success Icon */}
          <div 
            className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-4"
            style={{ backgroundColor: primaryColor + '15' }}
          >
            <CheckCircle className="w-12 h-12" strokeWidth={1.5} style={{ color: primaryColor }} />
          </div>

          {/* Success Message */}
          <div className="space-y-4">
            <h2 className="text-4xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Formulaire envoyé !
            </h2>
            <p className="text-lg text-foreground/80">
              Merci pour votre temps. Votre formulaire a été enregistré avec succès.
            </p>
            {submissionId && (
              <p className="text-sm text-foreground/60">
                Référence : <span className="mono font-medium">{submissionId}</span>
              </p>
            )}
          </div>

          {/* Email Confirmation */}
          <div className="bg-card border border-border rounded-lg p-6 text-left">
            <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Email de confirmation
            </h3>
            <p className="text-sm text-foreground/80">
              Un email de confirmation a été envoyé à votre adresse. Veuillez vérifier votre boîte de réception.
            </p>
          </div>

          {/* Review CTA */}
          <div 
            className="border rounded-lg p-6"
            style={{ backgroundColor: primaryColor + '08', borderColor: primaryColor + '30' }}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-6 h-6" strokeWidth={1.5} fill={primaryColor} style={{ color: primaryColor }} />
              ))}
            </div>
            <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Partagez votre expérience
            </h3>
            <p className="text-sm text-foreground/80 mb-4">
              Votre avis est important pour nous. Partagez votre expérience sur notre plateforme d'avis vérifiés.
            </p>
            <button
              data-testid="review-btn"
              onClick={handleReviewRedirect}
              className="w-full h-12 text-white rounded-md flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
            >
              <Star className="w-4 h-4" strokeWidth={1.5} />
              Laisser un avis
            </button>
          </div>

          {/* Back to Home */}
          <button
            data-testid="home-btn"
            onClick={() => {
              localStorage.removeItem('user_token');
              localStorage.removeItem('user_info');
              localStorage.removeItem('company_branding');
              navigate('/');
            }}
            className="w-full h-12 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" strokeWidth={1.5} />
            Retour à l'accueil
          </button>
        </div>
      </main>
    </div>
  );
}